import React, { useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Eye, EyeOff, ArrowLeft, User, Building2 } from 'lucide-react';
import { australianUniversities } from '../../data/universities';
import { useAuth } from '../../context/AuthContext';

const RegisterPage = () => {
  const { userType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Capture referral code from URL: /register/student?ref=ABCD1234 or via state
  const refFromQuery = new URLSearchParams(location.search).get('ref') || '';
  const refFromState = location.state?.referral_code || '';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const API = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    // Common fields
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    
    // Student specific
    university: '',
    course: '',
    yearOfStudy: '',
    studentId: '',
    
    // Landlord specific
    businessName: '',
    abn: '',
    licenseNumber: '',
    businessAddress: '',
    businessPhone: '',
    experienceYears: '',
    propertyTypes: []
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (step === 2 && userType === 'student') {
      if (!formData.university || !formData.yearOfStudy) {
        setError('Please select your university and year of study.');
        return;
      }
    }
    setError('');
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: `${formData.firstName} ${formData.lastName}`.trim(),
          role: userType === 'landlord' ? 'landlord' : 'student',
          referral_code: refFromQuery || refFromState || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === 'string' ? data.detail
          : Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(', ')
          : 'Registration failed. Please try again.';
        // Special case: email already registered — guide to login
        if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exist')) {
          setError('__EMAIL_EXISTS__');
        } else {
          setError(msg);
        }
        return;
      }

      // Auto-login after register
      const loginForm = new URLSearchParams();
      loginForm.append('username', formData.email);
      loginForm.append('password', formData.password);

      const loginRes = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: loginForm.toString(),
      });
      const loginData = await loginRes.json();

      if (loginRes.ok) {
        login(loginData.access_token, {
          id: loginData.user_id,
          email: loginData.email,
          full_name: loginData.full_name,
          role: loginData.role,
        });
        navigate(userType === 'landlord' ? '/landlord-dashboard' : '/student-dashboard');
      } else {
        navigate('/login');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
          <Input
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="Enter first name"
            className="h-12 border-sky-200 focus:border-[#094f6f]"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
          <Input
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Enter last name"
            className="h-12 border-sky-200 focus:border-[#094f6f]"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
        <Input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Enter your email"
          className="h-12 border-sky-200 focus:border-[#094f6f]"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
        <Input
          type="tel"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          placeholder="+61 4XX XXX XXX"
          className="h-12 border-sky-200 focus:border-[#094f6f]"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Create a strong password"
            className="h-12 border-sky-200 focus:border-[#094f6f] pr-12"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
        <div className="relative">
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm your password"
            className="h-12 border-sky-200 focus:border-[#094f6f] pr-12"
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStudentStep2 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">University</label>
        <Select value={formData.university} onValueChange={(value) => handleSelectChange('university', value)}>
          <SelectTrigger className="h-12 border-sky-200 focus:border-[#094f6f]">
            <SelectValue placeholder="Select your university" />
          </SelectTrigger>
          <SelectContent>
            {australianUniversities.map((uni, index) => (
              <SelectItem key={index} value={uni.name}>
                {uni.name} - {uni.location}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Course of Study</label>
        <Input
          name="course"
          value={formData.course}
          onChange={handleInputChange}
          placeholder="e.g., Bachelor of Computer Science"
          className="h-12 border-sky-200 focus:border-[#094f6f]"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Year of Study</label>
        <Select value={formData.yearOfStudy} onValueChange={(value) => handleSelectChange('yearOfStudy', value)}>
          <SelectTrigger className="h-12 border-sky-200 focus:border-[#094f6f]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1st-year">1st Year</SelectItem>
            <SelectItem value="2nd-year">2nd Year</SelectItem>
            <SelectItem value="3rd-year">3rd Year</SelectItem>
            <SelectItem value="4th-year">4th Year</SelectItem>
            <SelectItem value="postgraduate">Postgraduate</SelectItem>
            <SelectItem value="phd">PhD</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Student ID</label>
        <Input
          name="studentId"
          value={formData.studentId}
          onChange={handleInputChange}
          placeholder="Your student ID number"
          className="h-12 border-sky-200 focus:border-[#094f6f]"
        />
      </div>
    </div>
  );

  const renderLandlordStep2 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Business/Company Name</label>
        <Input
          name="businessName"
          value={formData.businessName}
          onChange={handleInputChange}
          placeholder="Enter business name"
          className="h-12 border-sky-200 focus:border-[#094f6f]"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ABN</label>
          <Input
            name="abn"
            value={formData.abn}
            onChange={handleInputChange}
            placeholder="Australian Business Number"
            className="h-12 border-sky-200 focus:border-[#094f6f]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Real Estate License</label>
          <Input
            name="licenseNumber"
            value={formData.licenseNumber}
            onChange={handleInputChange}
            placeholder="License number (if applicable)"
            className="h-12 border-sky-200 focus:border-[#094f6f]"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
        <Input
          name="businessAddress"
          value={formData.businessAddress}
          onChange={handleInputChange}
          placeholder="Full business address"
          className="h-12 border-sky-200 focus:border-[#094f6f]"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Phone</label>
          <Input
            name="businessPhone"
            value={formData.businessPhone}
            onChange={handleInputChange}
            placeholder="Business phone number"
            className="h-12 border-sky-200 focus:border-[#094f6f]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Experience (Years)</label>
          <Select value={formData.experienceYears} onValueChange={(value) => handleSelectChange('experienceYears', value)}>
            <SelectTrigger className="h-12 border-sky-200 focus:border-[#094f6f]">
              <SelectValue placeholder="Years of experience" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0-1">Less than 1 year</SelectItem>
              <SelectItem value="1-3">1-3 years</SelectItem>
              <SelectItem value="3-5">3-5 years</SelectItem>
              <SelectItem value="5-10">5-10 years</SelectItem>
              <SelectItem value="10+">10+ years</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Link to="/login" className="flex items-center text-[#094f6f] hover:text-[#094f6f]/80 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Link>

        <Card className="border-sky-100 shadow-xl">
          <CardHeader className="text-center pb-4">
            <Link to="/" className="inline-block mb-4">
              <div className="flex items-center justify-center gap-0 mb-4">
                <span className="text-3xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
                <img
                  src="/rentalx-x-new.svg"
                  alt="X"
                  style={{ height: '2.2rem', width: 'auto', marginLeft: '0.06em', marginBottom: '0.03em', verticalAlign: 'middle' }}
                />
              </div>
            </Link>
            <div className="flex items-center justify-center mb-4">
              {userType === 'student' ? <User className="w-6 h-6 text-[#094f6f] mr-2" /> : <Building2 className="w-6 h-6 text-[#094f6f] mr-2" />}
              <CardTitle className="text-2xl text-gray-800">
                Create {userType === 'student' ? 'Student' : 'Landlord'} Account
              </CardTitle>
            </div>
            <div className="flex justify-center space-x-2 mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`w-3 h-3 rounded-full ${step >= s ? 'bg-[#094f6f]' : 'bg-gray-300'}`} />
              ))}
            </div>
            <p className="text-gray-600">
              Step {step} of 3 — {step === 1 ? 'Basic Information' : step === 2 ? (userType === 'student' ? 'Academic Details' : 'Business Details') : 'Review & Submit'}
            </p>
          </CardHeader>

          <CardContent>
            {error === '__EMAIL_EXISTS__' ? (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="register-error">
                <p className="text-sm font-semibold text-amber-800 mb-1">This email is already registered.</p>
                <p className="text-sm text-amber-700 mb-3">You already have a RentalX account with this email address.</p>
                <div className="flex gap-2 flex-wrap">
                  <Link to={`/login`}
                    state={{ prefillEmail: formData.email }}
                    className="inline-block bg-[#094f6f] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#094f6f]/90 transition-colors">
                    Sign In Instead →
                  </Link>
                  <button type="button" onClick={() => { setError(''); setStep(1); }}
                    className="inline-block border border-[#094f6f] text-[#094f6f] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-sky-50 transition-colors">
                    Use a Different Email
                  </button>
                </div>
              </div>
            ) : error ? (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="register-error">
                {error}
              </div>
            ) : null}

            <form onSubmit={step === 3 ? handleSubmit : handleNext}>
              {step === 1 && renderStep1()}
              {step === 2 && userType === 'student' && renderStudentStep2()}
              {step === 2 && userType === 'landlord' && renderLandlordStep2()}
              {step === 3 && (
                <div className="text-center space-y-4">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Ready to create your account</h3>
                    <p className="text-gray-600">
                      {userType === 'student'
                        ? 'Your student account will be created immediately.'
                        : 'Your landlord account will be created immediately.'}
                    </p>
                  </div>
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                    <p className="text-sm text-[#094f6f]">
                      Name: {formData.firstName} {formData.lastName}<br />
                      Email: {formData.email}<br />
                      Role: {userType === 'student' ? 'Student' : 'Landlord'}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <Button type="button" onClick={handleBack} variant="outline" className="border-sky-200 text-[#094f6f] hover:bg-sky-50">
                    Previous
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className={`bg-[#094f6f] text-white hover:bg-[#094f6f]/90 ${step === 1 ? 'ml-auto' : ''}`}
                  data-testid="register-submit"
                >
                  {loading ? 'Creating...' : step === 3 ? 'Create Account' : 'Continue'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;